import {
    Button,
    Card,
    List,
    Space,
    Table,
    Tag,
    useModal,
} from "@pankod/refine-antd";
import {RedoOutlined} from '@ant-design/icons';
import {useApiUrl, useCustom} from "@pankod/refine-core";
import {IOrder} from "Interfaces";
import React, {useState} from "react";
import {OrderStatusTagRender} from "../Orders/OrderStatusTagRender";
import {formatCurrency} from "Pages/Dashboard/FormatCurrency";
import toast from "react-hot-toast";
import {RefundModalForm} from "../Orders/RefundModalForm";
import {processRefund} from "../../Services/paymentServices";
import {updateOrderMeta} from "../../Services/productServices";
import axios from "axios";

const { REACT_APP_STRAPI_TOKEN_KEY } = process.env;
const getRetryAxiosConfig = () => {
    const token = localStorage.getItem(`${REACT_APP_STRAPI_TOKEN_KEY}`);
    if (!token) {
        return {};
    }
    return {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    };
};

export const ProblemOrderList: React.FC = () => {
    const apiUrl = useApiUrl();
    const [currentRefundId, setCurrentRefundId] = useState<string | undefined>(undefined);
    const [currentRefundUid, setCurrentRefundUid] = useState<string | undefined>(undefined);

    const {
        data: responseData,
        isLoading,
        refetch,
    } = useCustom<{ data: IOrder[]; total: number }>({
        url: `${apiUrl}/checkouts/problem-orders`,
        method: "get",
    });

    const {
        modalProps: refundModalProps,
        show: refundModalShow,
        close: refundModalClose,
    } = useModal();

    const problemOrders = responseData?.data?.data ?? [];
    const total = responseData?.data?.total ?? 0;

    const handleRetry = async (record: IOrder) => {
        try {
            await toast.promise(
                axios.post(`${apiUrl}/checkouts/retry-pronto-order/${record.uid}`, {}, getRetryAxiosConfig()),
                {
                    loading: `Retrying Pronto order for ${record.uid}`,
                    success: `Retry succeeded for ${record.uid}`,
                    error: (error) => `Retry failed: ${error?.response?.data?.message || error.message}`,
                }
            );
            refetch();
        } catch (e) {
            console.error("Retry error:", e);
        }
    };

    const handleOnClickRefund = (record: IOrder) => {
        setCurrentRefundId(record.id?.toString());
        setCurrentRefundUid(record.uid);
        refundModalShow();
    };

    const handleRefundSuccess = async (data: { refundAmount: number; refundReason: string }) => {
        refundModalClose();

        await toast.promise(
            processRefund({
                ...data,
                uid: currentRefundUid,
            }),
            {
                loading: `Processing refund for ${currentRefundUid}`,
                success: `Refund processed ${currentRefundUid}`,
                error: (error) => `${error}`,
            }
        ).then(async () => {
            if (currentRefundId) {
                await toast.promise(
                    updateOrderMeta(Number(currentRefundId), 'total_refund', data.refundAmount),
                    {
                        loading: `Updating refund logs`,
                        success: `Updated refund logs`,
                        error: (error) => `${error}`,
                    }
                );
            }
            refetch();
        });
    };

    const getPaymentMethod = (record: IOrder) => {
        const payments = record.structuredData?.payments;
        if (Array.isArray(payments) && payments.length > 0) {
            return payments[0].type;
        }
        return "-";
    };

    const getPaymentAmount = (record: IOrder) => {
        const payments = record.structuredData?.payments;
        if (Array.isArray(payments) && payments.length > 0) {
            return payments[0].amount;
        }
        return 0;
    };

    const getError = (record: IOrder) => {
        return record.structuredData?.cms_errors || "-";
    };

    return (
        <>
            <Card title={`Problem Orders (${total})`}>
                <List>
                    <Table
                        dataSource={problemOrders}
                        loading={isLoading}
                        rowKey="id"
                        pagination={{
                            total,
                            pageSize: 50,
                        }}
                    >
                        <Table.Column
                            dataIndex="uid"
                            title="UID"
                            align="center"
                        />

                        <Table.Column<IOrder>
                            dataIndex="createdAt"
                            title="Date"
                            align="center"
                            render={(value) => new Date(value).toLocaleString()}
                        />

                        <Table.Column<IOrder>
                            dataIndex="emailAddress"
                            title="Email"
                            align="center"
                            render={(_, record) => record.structuredData?.email || record.emailAddress || "-"}
                        />

                        <Table.Column<IOrder>
                            dataIndex="totalValue"
                            title="Total"
                            align="center"
                            render={(_, record) => formatCurrency(record.structuredData?.total || record.totalValue || 0)}
                        />

                        <Table.Column<IOrder>
                            title="Payment Method"
                            align="center"
                            render={(_, record) => getPaymentMethod(record)}
                        />

                        <Table.Column<IOrder>
                            title="Payment Amount"
                            align="center"
                            render={(_, record) => formatCurrency(getPaymentAmount(record))}
                        />

                        <Table.Column<IOrder>
                            dataIndex="status"
                            title="Status"
                            align="center"
                            render={(value) => <OrderStatusTagRender status={value}/>}
                        />

                        <Table.Column<IOrder>
                            title="Error"
                            align="center"
                            render={(_, record) => (
                                <Tag color="red">{getError(record)}</Tag>
                            )}
                        />

                        <Table.Column<IOrder>
                            title="Actions"
                            align="center"
                            render={(_, record) => (
                                <Space>
                                    <Button
                                        type="primary"
                                        size="small"
                                        onClick={() => handleRetry(record)}
                                    >
                                        Retry
                                    </Button>
                                    <Button
                                        size="small"
                                        danger={true}
                                        onClick={() => handleOnClickRefund(record)}
                                    >
                                        <RedoOutlined/> Refund
                                    </Button>
                                </Space>
                            )}
                        />
                    </Table>
                </List>
            </Card>

            <RefundModalForm
                modalProps={refundModalProps}
                onSuccess={handleRefundSuccess}
            />
        </>
    );
};
